import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useParams } from "react-router";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/styles.min.css";

import { getDegree, getImageDegree, rotateImage } from "src/utils/images";
import { ASSETS_PROJECT_IMAGES_QUERY } from "src/constants/graphql/queries";

const StyledInnerImageZoom = styled(InnerImageZoom)(({ theme }) => ({
  maxHeight: "calc(100vh - 200px)",
  maxWidth: "100%",
}));

const styles = {
  content: {
    p: 0,
  },
  infoContainer: {
    textAlign: "center",
  },
  imageContainer: {
    position: "relative",
    textAlign: "center",
  },
  navButtonContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  navButton: {
    height: "100%",
    minHeight: "40px",
    minWidth: "40px",
    color: "#333",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
  dialogActions: {
    justifyContent: "flex-end",
    p: 1,
  },
};

const ProjectImageModal = ({
  open,
  edit = true,
  closeOnSave = false,
  selectedId: defaultId,
  onClose,
  onRemove,
  onUpdate,
  onCreate,
}) => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [combinedImages, setCombinedImages] = useState([]);
  const [selectedId, setSelectedId] = useState(defaultId);
  const [fetchData, setFetchData] = useState(false);

  const { loading: loadingAssets, data: assetsCollectionData } = useQuery(
    ASSETS_PROJECT_IMAGES_QUERY,
    {
      variables: { projectId },
      skip: !fetchData,
    }
  );

  useEffect(() => {
    if (open && projectId) {
      setFetchData(true);
    }
  }, [open, projectId]);

  useEffect(() => {
    if (assetsCollectionData && fetchData) {
      const assetsCollection = assetsCollectionData.assetsCollection || [];
      const images = assetsCollection.collection.flatMap(asset =>
        asset.images.map(image => ({
          ...image,
          assetId: asset.id,
          itemId: asset.itemId,
          manufacturer: asset.manufacturer,
          description: asset.description,
          dimensions: asset.dimensions,
          quantity: asset.quantity,
        }))
      );
      setCombinedImages(images);
      setSelectedId(images[0]?.id);
      setFetchData(false);
      setLoading(false);
    }
  }, [assetsCollectionData, fetchData]);

  const currentImage = combinedImages.find(image => selectedId === image.id) || combinedImages[0];
  const currentImageIndex = combinedImages.indexOf(currentImage);
  const InitialRotation = getImageDegree(currentImage?.url);
  const [rotate] = useState(InitialRotation);

  const prevImage = combinedImages[currentImageIndex - 1];
  const nextImage = combinedImages[currentImageIndex + 1];

  const imgUrl = rotateImage(currentImage?.url, "width", 1024, rotate);

  return (
    <Dialog fullWidth maxWidth="lg" open={open} onClose={onClose}>
      <DialogContent sx={styles.content}>
        <Grid container>
          <Grid size={12}>
            {currentImage && (
              <Box sx={styles.infoContainer}>
                <Typography variant="h4" style={{ color: "black" }}>
                  {currentImage.itemId}
                </Typography>
                <Typography variant="body1" style={{ color: "black" }}>
                  {currentImage.manufacturer} {currentImage.description} {currentImage.model}{" "}
                  {currentImage.dimensions?.[0]?.width}x{currentImage.dimensions?.[0]?.height}x
                  {currentImage.dimensions?.[0]?.depth} {currentImage.color}
                </Typography>
                <Typography variant="body1" style={{ color: "black" }}>
                  Quantity: {currentImage.quantity}
                </Typography>
              </Box>
            )}
            <Box display="flex" alignItems="center">
              <Box sx={styles.navButtonContainer} flexGrow={1}>
                <IconButton
                  sx={styles.navButton}
                  disabled={loading || !prevImage}
                  onClick={() => setSelectedId(prevImage?.id)}
                >
                  <NavigateBeforeIcon />
                </IconButton>
              </Box>
              {currentImage && (
                <Box
                  sx={styles.imageContainer}
                  flexGrow={1}
                  justifyContent="center"
                  mx={0.5}
                  borderRadius={4}
                >
                  <StyledInnerImageZoom src={imgUrl} zoomSrc={imgUrl} zoomScale={2} />
                </Box>
              )}
              <Box sx={styles.navButtonContainer} flexGrow={1}>
                <IconButton
                  sx={styles.navButton}
                  disabled={loading || !nextImage}
                  onClick={() => setSelectedId(nextImage?.id)}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectImageModal;
